/**
 * IT Checklist: the daily operational checklist completed by IT staff
 * (internet links, telephone line, Wi-Fi access points, CCTV, NVR backup).
 *
 * [SOT] This file is the single source of truth for the IT Checklist
 * contract shared by the API, the web app and the workbook generator:
 * field limits, the submit/overview/settings DTOs, the dashboard-period
 * bounds and default, the quarter model, the Excel header row, and the
 * Excel worksheet-name rules.
 *
 * The DATABASE is the authoritative record (`it_checklist_submissions`).
 * The quarterly Excel workbook in Azure Blob Storage is an export generated
 * from those rows and can be rebuilt at any time.
 *
 * Input types follow the existing workbook (Book1.xlsx, ~630 rows, Sep 2026):
 * "No. of Access Point Installed" was always a whole number there, so it is an
 * integer; every other operational field held descriptive text (e.g. "All AP
 * working", "NVR1 - 30 (1 NOT) NVR2 - 32", "NVR1&2 45 days NVR3&4 65 days"), so
 * those stay trimmed, required free text. "IT MANAGER COMMENTS" is
 * intentionally absent from this module (removed by requirement).
 */
import { z } from 'zod';
import { zonedParts } from './invoice-dto.js';
/** True for a real calendar date in strict `YYYY-MM-DD` form (rejects 2026-02-30). */
export function isValidIsoDate(v) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (!m)
        return false;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const dt = new Date(Date.UTC(y, mo - 1, d));
    return y >= 2000 && y <= 2100 && dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}
// ---------------------------------------------------------------------------
// Field limits
// ---------------------------------------------------------------------------
/** Short operational status fields (the source workbook's longest value was 60 characters). */
export const IT_CHECKLIST_SHORT_TEXT_MAX = 500;
/** "COMMENTS /ISSUES WITH LINKS AND CCTV" and "Engineer Comments". */
export const IT_CHECKLIST_LONG_TEXT_MAX = 2000;
/** "No. of Access Point Installed": whole number 0..IT_CHECKLIST_ACCESS_POINTS_MAX. */
export const IT_CHECKLIST_ACCESS_POINTS_MAX = 9999;
/** Status chosen beside Internet Link 1, Internet Link 2 and Telephone Line. */
export const IT_CHECKLIST_LINK_STATUSES = ['WORKING', 'NOT_WORKING'];
export const IT_CHECKLIST_LINK_STATUS_LABELS = {
    WORKING: 'Working',
    NOT_WORKING: 'Not Working',
};
/** Internet provider choices for Internet Provider 1 and 2 (stored as their label). */
export const IT_CHECKLIST_INTERNET_PROVIDERS = ['TATA_TELE', 'AIRTEL', 'JIO', 'ACT_BROADBAND', 'OTHER_BROADBAND'];
export const IT_CHECKLIST_INTERNET_PROVIDER_LABELS = {
    TATA_TELE: 'TATA-TELE',
    AIRTEL: 'AIRTEL',
    JIO: 'JIO',
    ACT_BROADBAND: 'ACT BROADBAND',
    OTHER_BROADBAND: 'OTHER BROADBAND',
};
/** Reverse lookup for a stored label (legacy free text returns null). */
export function internetProviderFromLabel(label) {
    if (!label)
        return null;
    const hit = Object.entries(IT_CHECKLIST_INTERNET_PROVIDER_LABELS).find(([, l]) => l === label);
    return hit ? hit[0] : null;
}
/** "No. of CCTV Cameras Installed": whole number 0..IT_CHECKLIST_CCTV_INSTALLED_MAX. */
export const IT_CHECKLIST_CCTV_INSTALLED_MAX = 9999;
/** Detailed history shown to the engineer: today and the previous 4 calendar days. Fixed; NOT tied to the dashboard period. */
export const IT_CHECKLIST_RECENT_DAYS = 5;
/** Dashboard history period ("Checklists Submitted — Last N Days"). One SOT for the default and bounds. */
export const IT_CHECKLIST_DASHBOARD_DAYS_DEFAULT = 90;
export const IT_CHECKLIST_DASHBOARD_DAYS_MIN = 1;
export const IT_CHECKLIST_DASHBOARD_DAYS_MAX = 365;
/** Quick picks offered by the Settings UI (any integer within the bounds is accepted). */
export const IT_CHECKLIST_DASHBOARD_DAYS_PRESETS = [30, 60, 90, 120, 180];
/** `system_settings.key` for one organisation's IT Checklist settings (org-scoped key; value is `StoredItChecklistSettings`). */
export function itChecklistSettingsKey(orgId) {
    return `it_checklist.settings:${orgId}`;
}
export const storedItChecklistSettingsSchema = z.object({
    dashboardHistoryDays: z.number().int().min(IT_CHECKLIST_DASHBOARD_DAYS_MIN).max(IT_CHECKLIST_DASHBOARD_DAYS_MAX),
});
/** Reads a stored settings value defensively: anything missing/invalid falls back to the defaults. */
export function readStoredItChecklistSettings(value) {
    const parsed = storedItChecklistSettingsSchema.safeParse(value);
    return parsed.success ? parsed.data : { dashboardHistoryDays: IT_CHECKLIST_DASHBOARD_DAYS_DEFAULT };
}
// ---------------------------------------------------------------------------
// Submit DTO (engineer-entered values only)
// ---------------------------------------------------------------------------
/**
 * Characters that are illegal in XML 1.0 (hence in .xlsx) or are C0/C1 control
 * characters. Tab, LF and CR are allowed. One U+FFFE in one submission would
 * otherwise make every later build of that quarter's workbook unreadable.
 */
// eslint-disable-next-line no-control-regex
export const IT_CHECKLIST_DISALLOWED_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\uD800-\uDFFF\uFFFE\uFFFF]/u;
/** Same set, global, for stripping (defence in depth in the generator). */
// eslint-disable-next-line no-control-regex
export const IT_CHECKLIST_DISALLOWED_CHARS_GLOBAL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\uFFFE\uFFFF]/g;
const UNSUPPORTED_CHARS_MESSAGE = 'Remove unsupported characters.';
const linkStatus = (label) => z.enum(IT_CHECKLIST_LINK_STATUSES, {
    errorMap: () => ({ message: `Choose Working or Not Working for ${label}.` }),
});
const requiredText = (label, max) => z
    .string({ required_error: `${label} is required.`, invalid_type_error: `${label} is required.` })
    .max(max * 2, `Use ${max} characters or fewer.`)
    .transform((v) => v.trim())
    .pipe(z
    .string()
    .min(1, `${label} is required.`)
    .max(max, `Use ${max} characters or fewer.`)
    .refine((v) => !IT_CHECKLIST_DISALLOWED_CHARS.test(v), UNSUPPORTED_CHARS_MESSAGE));
/**
 * What the browser may send. Engineer identity, email, organisation, checklist
 * date and submission time are NEVER accepted from the client: the server
 * derives them from the authenticated session and its own clock. Unknown keys
 * (e.g. `engineerEmail`, `submittedAt`, `itManagerComments`) are rejected.
 */
export const itChecklistSubmitDtoSchema = z
    .object({
    officeLocationId: z.string({ required_error: 'Location is required.' }).uuid('Choose a location.'),
    /** Internet Provider 1 — chosen from IT_CHECKLIST_INTERNET_PROVIDERS (hotfix 20 replaced the free-text field). */
    internetProvider1: z.enum(IT_CHECKLIST_INTERNET_PROVIDERS, { errorMap: () => ({ message: 'Choose Internet Provider 1.' }) }),
    internetLinkStatus: linkStatus('Internet Provider 1'),
    internetProvider2: z.enum(IT_CHECKLIST_INTERNET_PROVIDERS, { errorMap: () => ({ message: 'Choose Internet Provider 2.' }) }),
    internetLink2Status: linkStatus('Internet Provider 2'),
    /** Telephone Line is a status only (no text field since hotfix 20). */
    telephoneLineStatus: linkStatus('Telephone Line'),
    accessPointsInstalled: z
        .number({ required_error: 'No. of Access Point Installed is required.', invalid_type_error: 'Enter a whole number.' })
        .int('Enter a whole number.')
        .min(0, 'Enter 0 or more.')
        .max(IT_CHECKLIST_ACCESS_POINTS_MAX, `Enter ${IT_CHECKLIST_ACCESS_POINTS_MAX} or less.`),
    accessPointsWorking: requiredText('No. of Access Point Working', IT_CHECKLIST_SHORT_TEXT_MAX),
    cctvCamerasInstalled: z
        .number({ required_error: 'No. of CCTV Cameras Installed is required.', invalid_type_error: 'Enter a whole number.' })
        .int('Enter a whole number.')
        .min(0, 'Enter 0 or more.')
        .max(IT_CHECKLIST_CCTV_INSTALLED_MAX, `Enter ${IT_CHECKLIST_CCTV_INSTALLED_MAX} or less.`),
    cctvCamerasWorking: requiredText('CCTV — How Many Cameras Working', IT_CHECKLIST_SHORT_TEXT_MAX),
    backupDays: requiredText('How Many Days CCTV Backup Is There', IT_CHECKLIST_SHORT_TEXT_MAX),
    /** OPTIONAL since hotfix 21: absent, null, '' or whitespace-only all become null. */
    linkCctvIssues: z
        .string()
        .max(IT_CHECKLIST_LONG_TEXT_MAX * 2)
        .nullish()
        .transform((v) => (v == null || v.trim() === '' ? null : v.trim()))
        .pipe(z
        .string()
        .max(IT_CHECKLIST_LONG_TEXT_MAX, `Use ${IT_CHECKLIST_LONG_TEXT_MAX} characters or fewer.`)
        .refine((v) => !IT_CHECKLIST_DISALLOWED_CHARS.test(v), UNSUPPORTED_CHARS_MESSAGE)
        .nullable()),
    /**
     * VON Data Backup Date — OPTIONAL calendar date `YYYY-MM-DD` (absent, null
     * or '' = not given). The server additionally rejects a date after today
     * (business-local).
     */
    vonBackupDate: z
        .string()
        .max(20)
        .nullish()
        .transform((v) => (v == null || v.trim() === '' ? null : v.trim()))
        .refine((v) => v === null || isValidIsoDate(v), 'Choose a valid date.'),
    /** Optional: absent, null, '' or whitespace-only all become null. */
    engineerComments: z
        .string()
        .max(IT_CHECKLIST_LONG_TEXT_MAX * 2)
        .nullish()
        .transform((v) => (v == null || v.trim() === '' ? null : v.trim()))
        .pipe(z
        .string()
        .max(IT_CHECKLIST_LONG_TEXT_MAX, `Use ${IT_CHECKLIST_LONG_TEXT_MAX} characters or fewer.`)
        .refine((v) => !IT_CHECKLIST_DISALLOWED_CHARS.test(v), UNSUPPORTED_CHARS_MESSAGE)
        .nullable()),
})
    .strict();
export const updateItChecklistSettingsDtoSchema = z
    .object({
    dashboardHistoryDays: z
        .number({ required_error: 'Enter a number of days.', invalid_type_error: 'Enter a whole number of days.' })
        .int('Enter a whole number of days.')
        .min(IT_CHECKLIST_DASHBOARD_DAYS_MIN, `Enter at least ${IT_CHECKLIST_DASHBOARD_DAYS_MIN} day.`)
        .max(IT_CHECKLIST_DASHBOARD_DAYS_MAX, `Enter ${IT_CHECKLIST_DASHBOARD_DAYS_MAX} days or fewer.`),
})
    .strict();
// ---------------------------------------------------------------------------
// Excel worksheet (tab) names
// ---------------------------------------------------------------------------
/** The mandatory main worksheet holding every submission of the quarter. */
export const IT_CHECKLIST_SYSTEM_SHEET_NAME = 'Daily Checklist';
export const EXCEL_SHEET_NAME_MAX = 31;
/** Characters Excel forbids in a worksheet name. */
export const EXCEL_SHEET_NAME_FORBIDDEN = /[\\/?*[\]:]/;
/** Maximum number of active custom tabs (keeps the workbook sensible). */
export const IT_CHECKLIST_CUSTOM_TABS_MAX = 20;
/** Maximum custom tabs ever created per organisation, active + disabled (tabs are never deleted; keeps reorder bounded). */
export const IT_CHECKLIST_CUSTOM_TABS_TOTAL_MAX = 200;
/** Earliest quarter a rebuild may target (the module did not exist before). */
export const IT_CHECKLIST_FIRST_YEAR = 2026;
/** Case-insensitive identity used for duplicate detection (Excel treats sheet names case-insensitively). */
export function sheetNameKey(name) {
    return name.trim().toLocaleLowerCase('en-US');
}
/** Validates one custom worksheet name per Excel's rules (after trimming). */
export const itChecklistTabNameSchema = z
    .string({ required_error: 'Enter a worksheet name.' })
    .max(200)
    .transform((v) => v.trim())
    .superRefine((v, ctx) => {
    if (v === '')
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a worksheet name.' });
    else if (v.length > EXCEL_SHEET_NAME_MAX)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Use ${EXCEL_SHEET_NAME_MAX} characters or fewer (Excel limit).` });
    else if (EXCEL_SHEET_NAME_FORBIDDEN.test(v))
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Worksheet names cannot contain \\ / ? * [ ] or :' });
    else if (v.startsWith("'") || v.endsWith("'"))
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Worksheet names cannot start or end with an apostrophe.' });
    else if (IT_CHECKLIST_DISALLOWED_CHARS.test(v) || /[\t\n\r]/.test(v))
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Worksheet names cannot contain control or unsupported characters.' });
    else if (sheetNameKey(v) === sheetNameKey(IT_CHECKLIST_SYSTEM_SHEET_NAME))
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `"${IT_CHECKLIST_SYSTEM_SHEET_NAME}" is the system worksheet.` });
    else if (sheetNameKey(v) === 'history')
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: '"History" is reserved by Excel.' });
});
export const createItChecklistTabDtoSchema = z.object({ name: itChecklistTabNameSchema }).strict();
/** Rename and/or enable/disable. At least one field. */
export const updateItChecklistTabDtoSchema = z
    .object({ name: itChecklistTabNameSchema.optional(), isActive: z.boolean().optional() })
    .strict()
    .refine((v) => v.name !== undefined || v.isActive !== undefined, { message: 'Nothing to change.' });
/** Full new order of the organisation's custom tabs (every tab id exactly once). */
export const reorderItChecklistTabsDtoSchema = z.object({ ids: z.array(z.string().uuid()).min(1).max(200) }).strict();
export const rebuildItChecklistWorkbookDtoSchema = z
    .object({ year: z.number().int().min(IT_CHECKLIST_FIRST_YEAR).max(2100), quarter: z.number().int().min(1).max(4) })
    .strict();
// ---------------------------------------------------------------------------
// Excel main-sheet header row (exact wording kept for compatibility, incl. "CHEKED")
// ---------------------------------------------------------------------------
export const IT_CHECKLIST_EXCEL_HEADERS = [
    'LOCATION',
    'ENGINEER EMAIL ID',
    'CHEKED TIME AND DATE',
    'INTERNET PROVIDER 1',
    'INTERNET PROVIDER 1 STATUS',
    'INTERNET PROVIDER 2',
    'INTERNET PROVIDER 2 STATUS',
    'TELEPHONE LINE STATUS',
    'No. of Access Point Installed',
    'No. of Access Point Working',
    'No. of CCTV Cameras Installed',
    'CCTV HOW MANY CAMERAS WORKING',
    'HOW MANY DAYS CCTV BACKUP IS THERE',
    'VON DATA BACKUP DATE',
    'COMMENTS /ISSUES WITH LINKS AND CCTV',
    'Engineer Comments',
];
/** UI labels (the UI may use friendlier wording than the export). */
export const IT_CHECKLIST_FIELD_LABELS = {
    location: 'Location',
    engineerEmail: 'Engineer',
    checkedAt: 'Checked Time and Date',
    internetProvider1: 'Internet Provider 1',
    internetLinkStatus: 'Internet Provider 1 Status',
    internetProvider2: 'Internet Provider 2',
    internetLink2Status: 'Internet Provider 2 Status',
    telephoneLineStatus: 'Telephone Line',
    accessPointsInstalled: 'No. of Access Point Installed',
    accessPointsWorking: 'No. of Access Point Working',
    cctvCamerasInstalled: 'No. of CCTV Cameras Installed',
    cctvCamerasWorking: 'CCTV — How Many Cameras Working',
    backupDays: 'How Many Days CCTV Backup Is There',
    vonBackupDate: 'VON Data Backup Date',
    linkCctvIssues: 'Comments / Issues With Links and CCTV',
    engineerComments: 'Engineer Comments',
};
// ---------------------------------------------------------------------------
// Dates and quarters (business-local, APP_TIME_ZONE)
// ---------------------------------------------------------------------------
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function pad2(n) {
    return String(n).padStart(2, '0');
}
/** `YYYY-MM-DD` of `instant` in IANA zone `timeZone` — the checklist date. */
export function businessDate(instant, timeZone) {
    const p = zonedParts(instant, timeZone);
    return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}
/** Parses a strict `YYYY-MM-DD` calendar date (throws on anything else, incl. 2026-02-30). */
export function parseIsoDate(iso) {
    const m = ISO_DATE_RE.exec(iso);
    if (!m)
        throw new Error(`Invalid ISO date: ${iso}`);
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const d = new Date(Date.UTC(year, month - 1, day));
    if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day)
        throw new Error(`Invalid ISO date: ${iso}`);
    return { year, month, day };
}
/** Pure calendar arithmetic on `YYYY-MM-DD` (no time zone involved). */
export function addDaysIso(iso, days) {
    const { year, month, day } = parseIsoDate(iso);
    const d = new Date(Date.UTC(year, month - 1, day + days));
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}
const QUARTER_END = { 1: '03-31', 2: '06-30', 3: '09-30', 4: '12-31' };
/** The fixed calendar quarter (Q1 Jan–Mar, Q2 Apr–Jun, Q3 Jul–Sep, Q4 Oct–Dec). */
export function quarterOf(year, quarter) {
    if (!Number.isInteger(year) || year < 2000 || year > 2100)
        throw new Error(`Invalid year: ${year}`);
    if (quarter !== 1 && quarter !== 2 && quarter !== 3 && quarter !== 4)
        throw new Error(`Invalid quarter: ${quarter}`);
    const q = quarter;
    const startMonth = (q - 1) * 3 + 1;
    return {
        year,
        quarter: q,
        key: `${year}-Q${q}`,
        startDate: `${year}-${pad2(startMonth)}-01`,
        endDate: `${year}-${QUARTER_END[q]}`,
        fileName: `IT-Checklist-${year}-Q${q}.xlsx`,
    };
}
/** The quarter a checklist date (`YYYY-MM-DD`, business-local) belongs to. The DB date is authoritative, never a file name. */
export function quarterForDate(checklistDate) {
    const { year, month } = parseIsoDate(checklistDate);
    return quarterOf(year, Math.floor((month - 1) / 3) + 1);
}
/**
 * Blob key of one organisation's quarterly workbook:
 * `it-checklists/{orgId}/{year}/Q{n}/IT-Checklist-{year}-Q{n}.xlsx`.
 * Org-scoped so tenants can never share a workbook. Built only from a
 * DB-sourced UUID and integers (no client-authored string).
 */
export function itChecklistWorkbookBlobKey(orgId, q) {
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(orgId))
        throw new Error('orgId must be a UUID');
    const quarter = quarterOf(q.year, q.quarter);
    return `it-checklists/${orgId.toLowerCase()}/${quarter.year}/Q${quarter.quarter}/${quarter.fileName}`;
}
export const IT_CHECKLIST_MONITOR_SORTS = ['submittedAt', 'location', 'engineer'];
export const IT_CHECKLIST_MONITOR_MAX_RANGE_DAYS = 366;
const isoDateParam = z.string().refine(isValidIsoDate, 'Use a valid date (YYYY-MM-DD).');
/** GET /it-checklist/monitor/submissions query. Dates are checklist dates (inclusive); both default to today on the server. */
export const itChecklistMonitorQuerySchema = z
    .object({
    from: isoDateParam.optional(),
    to: isoDateParam.optional(),
    officeLocationId: z.string().uuid().optional(),
    engineerId: z.string().uuid().optional(),
    /** Engineer name / email / location code or name contains (case-insensitive). */
    q: z.string().trim().max(100).refine((v) => !IT_CHECKLIST_DISALLOWED_CHARS.test(v), 'Remove unsupported characters.').optional(),
    sort: z.enum(IT_CHECKLIST_MONITOR_SORTS).default('submittedAt'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    page: z.coerce.number().int().min(1).max(10000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
    .strict()
    .refine((v) => !v.from || !v.to || v.from <= v.to, { message: '"From" must be on or before "To".', path: ['from'] });
export const itChecklistMonitorSummaryQuerySchema = z.object({ date: isoDateParam.optional() }).strict();
/** SSE event name on GET /it-checklist/monitor/stream (data = ItChecklistMonitorRowDto JSON). */
export const IT_CHECKLIST_MONITOR_EVENT = 'itChecklist.submitted';
//# sourceMappingURL=it-checklist.js.map